<template>
  <BModal
    v-model="modal"
    :title="`Add Assignment for ${weeklyOutlookValue}`"
    ref="modalRef"
    ok-title="Save"
    ok-variant="success"
    @ok="handleAssignmentSubmission"
  >
    <div class="text-center">
      <BFormGroup
        id="input-group-amount"
        label="Amount:"
        label-for="amount-input"
        class="floating-group"
      >
        <BFormInput
          id="amount-input"
          v-model="amount"
          type="number"
          step="0.01"
          :class="{ 'is-invalid': amountError !== null }"
          placeholder="0.00"
          style="font-size: 0.78rem"
        />
      </BFormGroup>
      <small class="text-muted">
        <strong>Note:</strong> Amount will be stored as the original transaction
        amount as well.
      </small>
      <BFormGroup
        id="input-group-description"
        label="Description:"
        label-for="description-input"
        class="floating-group"
      >
        <BFormTextarea
          id="description-input"
          v-model="description"
          :class="{ 'is-invalid': descriptionError !== null }"
          placeholder="Enter something..."
          rows="2"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
    </div>
  </BModal>
</template>
<script>
import {
  BModal,
  BFormGroup,
  BFormInput,
  BFormTextarea,
} from "bootstrap-vue-next";
import { userSession as userSessNs } from "@/store/namespaces";

export default {
  name: "CreateAssignmentModal",
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BFormTextarea,
  },
  props: {
    acctUid: {
      type: String,
      required: true,
    },
    weeklyOutlookValue: {
      type: String,
      required: true,
    },
  },
  emits: ["assignmentCreated"],
  data() {
    return {
      modal: false,
      amount: null,
      amountError: null, // Is required and can't be 0
      description: "Previous week's leftover", // Default description for manually created assignments, but user can change it
      descriptionError: null, // Is required
    };
  },
  methods: {
    validateForm() {
      let isValid = true;

      if (this.amount === null || this.amount === 0) {
        this.amountError = "Amount is required and cannot be 0.";
        isValid = false;
      } else {
        this.amountError = null;
      }

      if (!this.description) {
        this.descriptionError = "Description is required.";
        isValid = false;
      } else {
        this.descriptionError = null;
      }

      return isValid;
    },
    async handleAssignmentSubmission(bvEvt) {
      try {
        bvEvt.preventDefault(); // Prevent the modal from closing immediately
        const formIsValid = this.validateForm();
        if (!formIsValid) return;

        if (!this.acctUid) {
          this.$notify.notifyError(
            "Account context is missing. Please select an account and try again."
          );
          return;
        }

        const userInfo = await this.$store.dispatch(
          `${userSessNs}/getUserInfo`
        );
        if (!userInfo?.id) {
          this.$notify.notifyError(
            "User information is missing. Please log in again and try."
          );
          return;
        }

        await this.$api.post(
          `/weekly-outlooks/${this.weeklyOutlookValue}/assignments`,
          {
            acct_uid: this.acctUid,
            user_uid: userInfo.id,
            assignments: [
              {
                amount: Number(this.amount).toFixed(2), // Ensure amount is sent as a string with 2 decimal places
                orig_trsxn_amt: Number(this.amount).toFixed(2),
                orig_trsxn_desc: this.description,
                orig_trsxn_uid: `00000000-0000-0000-0000-000000000000`, // Use a placeholder UUID since this transaction doesn't exist in the backend as a real transaction. This field is required by the backend but doesn't have meaning for manually created assignments.
              },
            ],
          }
        );

        this.$refs.modalRef.hide();
        this.$notify.notifySuccess("Assignment saved successfully.");
        this.$emit("assignmentCreated");
        this.resetModalState();
      } catch (error) {
        console.error("Error submitting assignment:", error);
        this.$notify.notifyError(
          "An error occurred while saving the assignment. Please try again."
        );
      }
    },
    resetModalState() {
      this.amount = null;
      this.description = "Previous week's leftover";
      this.amountError = null;
      this.descriptionError = null;
    },
  },
};
</script>
